import {
  broadcastToParent,
  RegisteredEventName
} from '@finanzcheck/catalyst-pollard'
import { logAndTrackError } from 'utils/log/logAndTrackError'
import { EntryPoint } from 'startup/command/loadEntryPoint'
import { logWarning } from 'utils/log'
import { getEnv } from 'utils/env'
import { getTrackingPath, isTenantSmava } from 'utils/url'
import { BestOffer } from 'components/BestOfferBox/BestOffer.interface'
import { COOKIE_KEY_FCID, getCookie } from 'utils/cookie'
import { Data } from 'interfaces/ApolloResponse.interface'

import {
  AFFILIATE_ID_FFG,
  AFFILIATE_ID_SMAVA,
  AffiliateEventName,
  sendAffiliateEvent
} from '../affiliate'
import { TransactionTeal } from './tracking.interface'
import RootStore from '../../stores/RootStore'
import { AmplitudeEvent } from './amplitude/amplitude.interface'
import { trackInAmplitude } from './amplitude'
import { isRceEditConfig } from 'config/utils/config'
import { getHighestRetirementPension } from 'components/RceEdit/utils'
import isRetired from 'utils/formDependencies/conditions/isRetired'
import { Debtor } from 'containers/PersonalForm/PersonalForm.interface'
import { trackDocumentDeliverySelection } from 'components/AbTestComponents/CAT4853/tracking'

const isSmavaTrackingEvent = isTenantSmava()

declare global {
  interface Window {
    // This function is only for the pre-rendering build step
    analytics: { page: (args: unknown) => void }
  }
}
interface tealProps {
  transaction: TransactionTeal
  affiliateId?: string | number | undefined
  subId?: string | number | undefined
  refId?: string | number | undefined
  configurationId?: string | number | undefined
  traversalPersonId?: string | number | undefined
  category?: string | undefined
  step?: string | undefined
  context?: Record<string, unknown>
  advertisementId?: string
  payload?: Record<string, unknown>
}

export function trackTeal({
  transaction,
  affiliateId,
  subId,
  refId,
  configurationId,
  traversalPersonId,
  step,
  context,
  payload
}: tealProps) {
  broadcastToParent({
    eventName: RegisteredEventName.tracking,
    data: {
      ...transaction,
      segmentWriteKey: getEnv('REACT_APP_SEGMENT_WRITE_KEY'),
      ...(affiliateId && { affiliateId }),
      ...(subId && { subId }),
      ...(refId && { refId }),
      ...(configurationId && { configId: configurationId }),
      ...(traversalPersonId && { personId: traversalPersonId }),
      step: step || getTrackingPath(),
      ...((getCookie(COOKIE_KEY_FCID) as string) && {
        fcId: getCookie(COOKIE_KEY_FCID) as string
      }),
      ...(context && {
        context
      }),
      ...(payload || {})
    }
  })
}

// The leads should already exist for these events, see CAT-3592
export const ignoredEntryPoints = [
  EntryPoint.LEAD_ABANDONED,
  EntryPoint.LEAD_ABORTED,
  EntryPoint.LEAD_SAVED
]

export const trackPayoutAccountPageView = async (rootStore: RootStore) => {
  const { initialEntryPoint, traversal } = rootStore

  const {
    traversalId,
    data: {
      common: { amount, purpose, term },
      system: {
        affiliateId = isSmavaTrackingEvent
          ? AFFILIATE_ID_SMAVA
          : AFFILIATE_ID_FFG,
        subId,
        refId,
        configurationId,
        advertisementId
      },
      debtors: { count }
    }
  } = traversal

  const transaction: TransactionTeal = {
    event: 'payout_account_page_view',
    label: getTrackingPath(),
    traversalId: traversalId || undefined,
    amount,
    purpose,
    term,
    category: 'loan_status',
    products: [
      {
        product_id: purpose,
        quantity: 1,
        price: amount
      }
    ]
  }

  try {
    trackTeal({
      transaction,
      affiliateId,
      subId,
      refId,
      configurationId,
      context: {
        Optimizely: { userId: getCookie(COOKIE_KEY_FCID) },
        userAttributes: {
          advertisementId,
          debtorCount: count
        }
      }
    })
  } catch (error) {
    logAndTrackError(new Error('Error when payout_account_page_view'))
  }

  if (initialEntryPoint && ignoredEntryPoints.includes(initialEntryPoint)) {
    logWarning(
      `Lead was not tracked because the entry point is "${initialEntryPoint}".`
    )
    return
  }
}

export const trackLead = async (rootStore: RootStore) => {
  const { initialEntryPoint, traversal } = rootStore

  const {
    traversalId,
    data: {
      common: { amount, purpose, term },
      system: {
        affiliateId = isSmavaTrackingEvent
          ? AFFILIATE_ID_SMAVA
          : AFFILIATE_ID_FFG,
        subId,
        refId,
        configurationId,
        advertisementId
      }
    }
  } = traversal

  const transaction: TransactionTeal = {
    event: 'all_lead',
    label: getTrackingPath(),
    traversalId: traversalId || undefined,
    amount,
    purpose,
    term,
    category: 'loan_status',
    products: [
      {
        product_id: purpose,
        quantity: 1,
        price: amount
      }
    ]
  }

  try {
    trackTeal({
      transaction,
      affiliateId,
      subId,
      refId,
      configurationId,
      context: {
        Optimizely: { userId: getCookie(COOKIE_KEY_FCID) },
        userAttributes: {
          advertisementId
        }
      }
    })

    const emailPrimaryDebtor = document.getElementsByName(
      'debtors.primary.contact.email'
    )[0] as HTMLButtonElement

    if (emailPrimaryDebtor && !emailPrimaryDebtor.disabled) {
      trackTeal({
        transaction: {
          event: 'virtual_lead_event'
        },
        affiliateId,
        subId,
        refId,
        configurationId,
        context: {
          Optimizely: { userId: getCookie(COOKIE_KEY_FCID) },
          userAttributes: {
            advertisementId
          }
        }
      })
      sendAffiliateEvent(AffiliateEventName.Conversion, traversal, rootStore)
    }
  } catch (error) {
    // CAT-3850 We throw this to be able to filter in Bugsnag
    logAndTrackError(new Error('Error when trackAllLead'))
  }

  if (initialEntryPoint && ignoredEntryPoints.includes(initialEntryPoint)) {
    logWarning(
      `Lead was not tracked because the entry point is "${initialEntryPoint}".`
    )
    return
  }
}

const trackEditPageFieldChanges = (rootStore: RootStore): void => {
  const {
    traversal: {
      data: {
        common: {
          amount,
          payoutAccount: { iban }
        },
        system: {
          affiliateId = isSmavaTrackingEvent
            ? AFFILIATE_ID_SMAVA
            : AFFILIATE_ID_FFG,
          subId,
          refId,
          configurationId,
          advertisementId
        },
        debtors: {
          primary: {
            personId: traversalPersonId,
            income,
            currentAddress: { street, houseNumber }
          },
          count
        }
      }
    },
    traversalSession: { rceEditInitialData }
  } = rootStore

  const isPensioner = isRetired(Debtor.PRIMARY)(rootStore.traversal.data)
  const currentPension = isPensioner
    ? getHighestRetirementPension(income.pensions ?? [])?.income
    : undefined

  const sharedTealProps = {
    affiliateId,
    subId,
    refId,
    configurationId,
    traversalPersonId,
    context: {
      Optimizely: { userId: getCookie(COOKIE_KEY_FCID) },
      userAttributes: {
        advertisementId,
        debtorCount: count
      }
    }
  }

  const editPageFieldChanges: Array<{
    event: AmplitudeEvent
    currentValue: unknown
    initialValue: unknown
    skip?: boolean
  }> = [
    {
      event: AmplitudeEvent.RceEditStreetChanged,
      currentValue: street,
      initialValue: rceEditInitialData.street
    },
    {
      event: AmplitudeEvent.RceEditStreetNumberChanged,
      currentValue: houseNumber,
      initialValue: rceEditInitialData.houseNumber
    },
    {
      event: AmplitudeEvent.RceEditIbanChanged,
      currentValue: iban,
      initialValue: rceEditInitialData.iban
    },
    {
      event: AmplitudeEvent.RceEditAmountChanged,
      currentValue: amount,
      initialValue: rceEditInitialData.amount
    },
    {
      event: AmplitudeEvent.RceEditIncomeChanged,
      currentValue: income.primary,
      initialValue: rceEditInitialData.income
    },
    {
      event: AmplitudeEvent.RceEditPensionChanged,
      currentValue: currentPension,
      initialValue: rceEditInitialData.pension,
      skip: !isPensioner
    }
  ]

  editPageFieldChanges.forEach(
    ({ event, currentValue, initialValue, skip }) => {
      if (!skip && initialValue != null && currentValue !== initialValue) {
        trackTeal({ transaction: { event }, ...sharedTealProps })
      }
    }
  )
}

/**
 * Fires tracking events immediately before the finalize POST is sent.
 */
export const trackPreFinalize = (rootStore: RootStore): void => {
  const {
    page: { config }
  } = rootStore

  if (isRceEditConfig(config?.name)) {
    trackEditPageFieldChanges(rootStore)
  } else {
    const {
      traversal: {
        data: {
          debtors: {
            primary: { hasAdditionalCitizenships }
          }
        }
      }
    } = rootStore
    trackInAmplitude(AmplitudeEvent.IsMultipleCitizenshipAddedOnFinal, {
      hasAdditionalCitizenships
    })
    trackDocumentDeliverySelection(rootStore)
  }
}

export const trackFinal = async ({ traversal }: RootStore) => {
  if (traversal?.data && traversal?.traversalId) {
    const {
      common: { productId, purpose, amount, term, amountAdaption },
      system: {
        affiliateId = isSmavaTrackingEvent
          ? AFFILIATE_ID_SMAVA
          : AFFILIATE_ID_FFG,
        subId,
        refId,
        configurationId,
        advertisementId
      },
      debtors: {
        primary: { personId: traversalPersonId, activeLoans },
        count
      }
    } = traversal.data

    const tealEventData = {
      event: 'final',
      label: '/kreditantrag/payoutAccount',
      traversalId: traversal?.traversalId || '',
      amount,
      purpose,
      term,
      category: 'loan_status',
      debtorCount: count
    }

    trackTeal({
      transaction: tealEventData,
      affiliateId,
      subId,
      refId,
      configurationId,
      traversalPersonId,
      context: {
        Optimizely: { userId: getCookie(COOKIE_KEY_FCID) },
        userAttributes: {
          advertisementId,
          debtorCount: count,
          amountAdaption,
          isDebtConversionWantedInAtLeastOneLoan: activeLoans?.some(
            (activeLoan) => activeLoan.isDebtConversionWanted
          )
        }
      }
    })
  }
}

export const trackBestOfferView = async (bestOffer: BestOffer) => {
  trackInAmplitude(AmplitudeEvent.DisplayBestOfferBox, {
    productId: bestOffer.product.id
  })
}

export const getOptimizelyTrackingAttribute = (data: Data) => {
  const userAttributesObject = {
    advertisementId: data.system.advertisementId || null
  }
  return userAttributesObject
}
